import {
  FETCH_INIT,
  FETCHED,
  FETCHED_FAIL,
  SET_SORTING,
  SET_FILTER,
  CHANGE_PAGE,
  SET_SERVER_PAGE,
  SET_ROW_SIZE,
  CLEAN_PROGRAM_SALES_REP,
} from "../actions/ProgramSalesRep.action";

function mapPresetPRequest(all, pageId, rowsPerPage = 10) {
  return all.filter((val, index) => {
    if (
      index >= (pageId + 1) * rowsPerPage - rowsPerPage &&
      index < (pageId + 1) * rowsPerPage
    ) {
      return val;
    }
  });
}

const initialState = {
  all: [],
  present: [],
  currentPage: 0,
  serverPage: 0,
  query: null,
  query_data: null,
  sorting_data: { row: null, order: null },
  is_fetching: false,
  rowsPerPage: 10,
  // true once the program is known to have at least one representative, so the
  // block is not hidden when a search/filter comes back empty
  has_records: false,
  // dropdown options for the rep/manager filters, collected from every response
  // so narrowing the list does not shrink the choices
  filter_options: { emp_name: [], client_managers: [] },
};

// placeholders the api sends when a rep has no manager mapped, they should not
// become a filter option
const BLANK_FILTER_VALUES = ["NA", "N/A", "N.A", "N.A.", "-", "--"];

function isBlankFilterValue(value) {
  if (!value) {
    return true;
  }
  return BLANK_FILTER_VALUES.indexOf(String(value).trim().toUpperCase()) > -1;
}

function mergeFilterOptions(existing, rows, key) {
  const merged = new Set(existing);
  (rows || []).forEach((row) => {
    const value = row?.[key];
    if (!isBlankFilterValue(value)) {
      merged.add(value);
    }
  });
  if (merged.size === existing.length) {
    return existing;
  }
  return Array.from(merged).sort((a, b) =>
    String(a).toLowerCase().localeCompare(String(b).toLowerCase())
  );
}

// the api now sends the area managers of a rep as an array, so the filter
// options are collected as {client_manager_id, client_manager_name} objects and
// the id is what gets sent back when filtering
function mergeManagerOptions(existing, rows) {
  const merged = [...existing];
  const seenIds = new Set(existing.map((val) => val?.client_manager_id));
  (rows || []).forEach((row) => {
    (row?.client_managers || []).forEach((manager) => {
      const id = manager?.client_manager_id;
      if (!id || seenIds.has(id) || isBlankFilterValue(manager?.client_manager_name)) {
        return;
      }
      seenIds.add(id);
      merged.push({
        client_manager_id: id,
        client_manager_name: manager.client_manager_name,
        client_manager_code: manager.client_manager_code,
      });
    });
  });
  if (merged.length === existing.length) {
    return existing;
  }
  return merged.sort((a, b) =>
    String(a?.client_manager_name)
      .toLowerCase()
      .localeCompare(String(b?.client_manager_name).toLowerCase())
  );
}

export default function (
  state = JSON.parse(JSON.stringify(initialState)),
  action
) {
  switch (action.type) {
    case FETCH_INIT: {
      return { ...state, is_fetching: true };
    }
    case FETCHED: {
      const newData = action.payload.data || [];
      const page = action.payload.page;
      let newAll = [];
      if (page == 1) {
        newAll = [...newData];
      } else {
        // guard against the same slab coming back twice, which would otherwise
        // duplicate rows in the table
        const loadedIds = new Set(state.all.map((val) => val?.id));
        newAll = [
          ...state.all,
          ...newData.filter((val) => !loadedIds.has(val?.id)),
        ];
      }
      const tableData = mapPresetPRequest(
        newAll,
        state.currentPage,
        state.rowsPerPage
      );
      return {
        ...state,
        all: newAll,
        present: tableData,
        is_fetching: false,
        has_records: state.has_records || newAll.length > 0,
        filter_options: {
          emp_name: mergeFilterOptions(
            state.filter_options.emp_name,
            newData,
            "emp_name"
          ),
          client_managers: mergeManagerOptions(
            state.filter_options.client_managers,
            newData
          ),
        },
      };
    }
    case FETCHED_FAIL: {
      return { ...state, is_fetching: false };
    }
    case SET_SORTING: {
      return { ...state, sorting_data: action.payload };
    }
    case SET_FILTER: {
      return {
        ...state,
        query: action.payload.query,
        query_data: action.payload.query_data,
      };
    }
    case CHANGE_PAGE: {
      const tempPage = action.payload;
      const tableData = mapPresetPRequest(
        state.all,
        tempPage,
        state.rowsPerPage
      );
      return { ...state, present: tableData, currentPage: tempPage };
    }
    case SET_SERVER_PAGE: {
      return { ...state, serverPage: action.payload };
    }
    case SET_ROW_SIZE: {
      const present = mapPresetPRequest(state.all, 0, action.payload);
      return { ...state, rowsPerPage: action.payload, present, currentPage: 0 };
    }
    case CLEAN_PROGRAM_SALES_REP: {
      return JSON.parse(JSON.stringify(initialState));
    }
    default: {
      return state;
    }
  }
}
